//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { CollectionStatusType } from '@apis/collections/CollectionStatusType';
import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { Caption } from '@components/atoms/caption/Caption';
import { LabelWithMargin } from '@components/atoms/label/Label';
import Media from '@components/atoms/media/Media';
import Product from '@components/atoms/products/Product';
import SecondaryAction from '@components/atoms/secondary-action/SecondaryAction';
import Source from '@components/atoms/source/Source';
import MediaCard from '@components/molecules/media-card/MediaCard';
import HorizontalScrolling from '@components/organisms/horizontal-scrolling/HorizontalScrolling';
import {
  useBoundActionCreators,
  useTypedSelector,
} from '@hooks/useCuralateStore';
import ClientId from '@models/ClientId';
import Cuid from '@models/Cuid';
import { UniversalItemType } from '@models/UniversalItemType';
import PartialCuid from '@models/PartialCuid';
import {
  getUniversalImageActionsView,
  getContentItemDetailsService,
} from '@react-bridge/wrappers/ng-service-provider';
import {
  buildRejectionReasonsActionSheetConfig,
  openUiaToView,
} from '@streams-drawer/streams-drawer-content/streams-drawer-moderation-content/streamsDrawerModerationUtils';
import { actions as streamDrawerActions } from '@streams/actions/streamDrawerActions';
import unisonTheme from '@theme/theme';
import { toLabelsArray } from '@utils/labelUtils';
import { isDefined } from '@utils/filterPredicates';
import { IContentItemDetails } from 'app/bulk-actions/model/AppState';
import { CAPTIONS_STATUS_VALUE } from 'app/constants/mute-tooltip-content';
import { Mixins } from '@theme/mixins';
interface IOwnProps {
  item: IContentLibraryItem;
  rejectItem: (cuid: Cuid, rejectionCode: number) => Promise<void>;
  disableRejection: boolean;
}

type Props = IOwnProps;

const StreamsDrawerMediaCard: React.FunctionComponent<Props> = ({
  item,
  rejectItem,
  disableRejection,
}) => {
  const { openRejectionReasonsModal, updateItemWithUiaUpdates } =
    useBoundActionCreators<typeof streamDrawerActions>(streamDrawerActions);
  const clientId = useTypedSelector((state) => state.session.client.id);
  const [isLabelListHovered, setIsLabelListHovered] = useState(false);
  const [isProductListHovered, setIsProductListHovered] = useState(false);
  const [isSecondaryActionOpen, setIsSecondaryActionOpen] = useState(false);
  const { moderationStatus, clientRejectionReasons } = useTypedSelector(
    (state) => state.streams.streamsDrawer,
  );

  const cuid = Cuid.build(item, ClientId.fromString(clientId));
  const labels = toLabelsArray(item?.labels ?? '');
  const rejectionReasonsActionSheetConfig =
    buildRejectionReasonsActionSheetConfig(
      cuid,
      clientRejectionReasons,
      openRejectionReasonsModal,
      rejectItem,
    );

  const UniversalImageActionsView = getUniversalImageActionsView();

  const showRejectionAction =
    !disableRejection && moderationStatus !== CollectionStatusType.Rejected;

  const networkDisplayUserName =
    item?.itemType === UniversalItemType.MediaImport
      ? item?.misDetails?.user?.fullName
      : item.socialDetails?.user?.fullName;

  const networkUsername =
    item?.itemType === UniversalItemType.MediaImport
      ? item?.misDetails?.user?.email
      : item.socialDetails?.user?.username;

  const [itemDetails, setItemDetails] = useState<IContentItemDetails>();

  const captionSelectedItems = useSelector(
    (state) => state.bulkActions.selectedItemsForCaptions,
  );
  const captionTurnOnOffState = useSelector(
    (state) => state.bulkActions.captionTurnOnOffState,
  );

  const captionStatusValue: string | undefined = captionSelectedItems[cuid];

  const isCaptionInProgress =
    captionTurnOnOffState === 'on' &&
    captionStatusValue === CAPTIONS_STATUS_VALUE.IN_PROGRESS;

  const isCaptioned =
    captionTurnOnOffState === 'on' &&
    captionStatusValue === CAPTIONS_STATUS_VALUE.SUCCEEDED;
  useEffect(() => {
    if (isCaptioned) {
      setItemDetails((prevItemDetails: any) => ({
        ...prevItemDetails,
        captionStatus: true,
      }));
    }
  }, [isCaptioned]);

  // Whenever the item changes, we'll need to hydrate it with some
  // additional metadata. This hook fetches item details and stores
  // them as state.
  useEffect(() => {
    if (item) {
      const contentLibraryItemDetailsService = getContentItemDetailsService();
      const partialCuid = PartialCuid.build(item);
      const itemDetailsStream =
        contentLibraryItemDetailsService.requestItemDetails(partialCuid, false);
      itemDetailsStream.subscribe((itemDetailsMap) => {
        const itemDetailsObj = itemDetailsMap && itemDetailsMap[partialCuid];
        if (itemDetailsObj) {
          setItemDetails(itemDetailsObj);
        }
      });
    }
  }, [item]);

  return (
    <StreamMediaCard
      itemType={item.itemType}
      id={item.id}
      image={
        <StreamMedia
          imageUrl={item.imageUrl}
          itemType={item.itemType}
          id={item.id}
          onItemClick={(e) => {
            openUiaToView(
              e,
              UniversalImageActionsView.Details.name,
              item.itemType,
              item.id,
              updateItemWithUiaUpdates,
            );
          }}
          metadataLayer={{
            enabled: true,
            isCaptionInProgress: isCaptionInProgress,
            itemDetails: itemDetails,
          }}
          actionLayer={(isMediaHovered: boolean) => (
            <>
              {(isMediaHovered || isSecondaryActionOpen) &&
                showRejectionAction && (
                  <SecondaryAction
                    onToggleActionSheet={(isOpen: boolean) =>
                      setIsSecondaryActionOpen(isOpen)
                    }
                    actionSheetConfig={rejectionReasonsActionSheetConfig}
                  />
                )}
            </>
          )}
        />
      }
      right={
        <StreamMetadataWrapper>
          <StreamSource
            universalItemType={item?.itemType}
            itemInfo={{
              originalUrl: item.socialDetails?.link,
              socialNetwork: item.socialDetails?.socialNetwork,
            }}
            userInfo={{
              networkUserImageUrl: item.socialDetails?.user?.profilePicture,
              networkUsername,
              networkUserDisplayName: networkDisplayUserName,
            }}
          />
          <StreamCaption
            maxPreviewLength={115}
            maxLineCount={3}
            caption={item.description || ''}
            shouldDecorateLinks={true}
            socialNetwork={item.socialDetails?.socialNetwork}
          />
          {labels?.length && (
            <StreamLabelList
              resetScrollDep={item.labels}
              header={
                <StreamHorizontalScrollingHeader>
                  <span>Labels ({labels?.length ?? 0})</span>
                  {isLabelListHovered && (
                    <StreamEditButton
                      onClick={(e) => {
                        openUiaToView(
                          e,
                          UniversalImageActionsView.Labels.name,
                          item.itemType,
                          item.id,
                          updateItemWithUiaUpdates,
                        );
                      }}
                    >
                      Edit Labels
                    </StreamEditButton>
                  )}
                </StreamHorizontalScrollingHeader>
              }
              onMouseEnter={() => setIsLabelListHovered(true)}
              onMouseLeave={() => setIsLabelListHovered(false)}
              overflowHorizontally={parseInt(unisonTheme.spacings[3], 10)}
              scrollDistance={248}
            >
              <StreamHorizontalScrollingItemsWrapper>
                {labels?.map((label, labelIdx) => (
                  <LabelWithMargin
                    shouldAppendTooltipToBody={true}
                    key={labelIdx}
                    label={label}
                  />
                ))}
              </StreamHorizontalScrollingItemsWrapper>
            </StreamLabelList>
          )}
          <StreamProductList
            resetScrollDep={item.products}
            header={
              <StreamHorizontalScrollingHeader>
                <span>Products ({item.products?.length ?? 0})</span>
                {isProductListHovered && (
                  <StreamEditButton
                    onClick={(e) => {
                      openUiaToView(
                        e,
                        UniversalImageActionsView.Productize.name,
                        item.itemType,
                        item.id,
                        updateItemWithUiaUpdates,
                      );
                    }}
                  >
                    Edit Products
                  </StreamEditButton>
                )}
              </StreamHorizontalScrollingHeader>
            }
            onMouseEnter={() => setIsProductListHovered(true)}
            onMouseLeave={() => setIsProductListHovered(false)}
            emptyState={<StreamNoProducts>No products added</StreamNoProducts>}
            overflowHorizontally={parseInt(unisonTheme.spacings[3], 10)}
            scrollDistance={248}
          >
            {isDefined(item.products) && item.products.length > 0 ? (
              <StreamHorizontalScrollingItemsWrapper>
                {item.products.map((product, productIdx) => (
                  <StreamProductItem
                    key={productIdx}
                    index={productIdx}
                    product={product}
                  />
                ))}
              </StreamHorizontalScrollingItemsWrapper>
            ) : null}
          </StreamProductList>
        </StreamMetadataWrapper>
      }
    />
  );
};

const StreamMetadataWrapper = styled.div(
  ({ theme }) => `
  padding: ${theme.spacings[3]} ${theme.spacings[3]} ${theme.spacings[4]} ${theme.spacings[3]};
`,
);

const StreamSource = styled(Source)(
  ({ theme }) => `
  margin-bottom: ${theme.spacings[2]};
`,
);

const StreamHorizontalScrollingHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StreamEditButton = styled.a.attrs({
  className: 'cu-text-link-primary',
  href: '#',
})``;

const StreamLabelList = styled(HorizontalScrolling)(
  ({ theme }) => `
  margin-bottom: calc(${theme.spacings[1]} * 3);
`,
);

const StreamProductList = styled(HorizontalScrolling)(
  ({ theme }) => `
  margin-bottom: ${theme.spacings[0]};
`,
);

const StreamProductItem = styled(Product)(
  ({ theme }) => `
  height: ${theme.spacings[7]};
  width: 240px;
  &:not(:first-of-type) {
    margin-left: ${theme.spacings[2]};
  }
`,
);

const StreamNoProducts = styled.div(
  ({ theme }) => `
  ${Mixins.Text.BodySecondary};
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${theme.spacings[7]};
  border: solid 1px ${theme.colors.gray2};
  border-radius: ${theme.borderRadii[2]};
  background: ${theme.colors.gray0};
`,
);

const StreamHorizontalScrollingItemsWrapper = styled.div`
  display: flex;
  flex-wrap: nowrap;
`;

const StreamCaption = styled(Caption)(
  ({ theme }) => `
  /* The caption should always be 3 lines of text tall */
  height: calc(${theme.lineHeights.bodyLeading} * 3em);
  margin-bottom: calc(${theme.spacings[1]} * 3);
`,
);

const StreamMediaCard = styled(MediaCard)(
  () => `
  height: 300px;
  width: 712px;
`,
);

const StreamMedia = styled(Media)(
  () => `
  height: 300px;
  width: 300px;
`,
);

export default StreamsDrawerMediaCard;
